import React, { useState, useEffect } from 'react';
import { upiAPI } from '../../services/api';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Building2,
  User,
  Loader,
  Smartphone,
  Shield
} from 'lucide-react';

const UpiIdDetailsTab = () => {
  const [upiIds, setUpiIds] = useState([]);
  const [filteredUpiIds, setFilteredUpiIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, partner, organization
  const [filterVerification, setFilterVerification] = useState('all'); // all, verified, unverified
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [selectedUpi, setSelectedUpi] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    loadUpiIds();
  }, []);

  useEffect(() => {
    filterUpiIds();
  }, [upiIds, searchTerm, filterType, filterVerification]);

  const loadUpiIds = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await upiAPI.getAllUpiIds();
      setUpiIds(response.data.data || []);
    } catch (err) {
      console.error('Failed to load UPI IDs:', err);
      setError(err.response?.data?.error || 'Failed to load UPI IDs');
    } finally {
      setLoading(false);
    }
  };

  const filterUpiIds = () => {
    let filtered = upiIds;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(u => u.type === filterType);
    }

    // Filter by verification status
    if (filterVerification !== 'all') {
      if (filterVerification === 'verified') {
        filtered = filtered.filter(u => u.upi_verified === true);
      } else if (filterVerification === 'unverified') {
        filtered = filtered.filter(u => u.upi_verified === false);
      }
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.name.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.contact?.includes(term) ||
        u.upi_id?.toLowerCase().includes(term)
      );
    }

    setFilteredUpiIds(filtered);
  };

  const handleOpenVerificationModal = (upi) => {
    setSelectedUpi(upi);
    setShowVerificationModal(true);
    setError('');
  };

  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
    setSelectedUpi(null);
    setError('');
  };

  const handleVerifyUpi = async () => {
    if (!selectedUpi) return;

    try {
      setVerifying(true);
      setError('');

      const response = await upiAPI.verifyUpi(
        selectedUpi.type,
        selectedUpi.id
      );

      if (response.data.success) {
        setSuccess(`UPI ID verified successfully for ${selectedUpi.name}`);
        handleCloseVerificationModal();
        // Reload UPI IDs to update verification status
        await loadUpiIds();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Failed to verify UPI ID:', err);
      setError(err.response?.data?.error || 'Failed to verify UPI ID');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600 dark:text-gray-400">Loading UPI ID details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">UPI ID Details</h1>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="card p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
            <CheckCircle className="h-5 w-5" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, contact, or UPI ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
            />
          </div>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
          >
            <option value="all">All Types</option>
            <option value="partner">Partners</option>
            <option value="organization">Organizations</option>
          </select>

          {/* Verification Status Filter */}
          <select
            value={filterVerification}
            onChange={(e) => setFilterVerification(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
          >
            <option value="all">All Status</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>
        </div>
      </div>

      {/* UPI IDs Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  UPI ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Verification Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-primary divide-y divide-gray-200 dark:divide-dark-border">
              {filteredUpiIds.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    No UPI IDs found
                  </td>
                </tr>
              ) : (
                filteredUpiIds.map((upi) => (
                  <tr key={`${upi.type}_${upi.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                        {upi.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {upi.type === 'partner' ? (
                          <User className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Building2 className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="text-sm text-gray-900 dark:text-dark-text-primary capitalize">
                          {upi.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-dark-text-primary">
                        {upi.organization_name || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900 dark:text-dark-text-primary">
                        {upi.upi_id || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {upi.upi_verified ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Not Verified
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <div>{upi.email || '-'}</div>
                        <div className="text-xs">{upi.contact || '-'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {!upi.upi_verified && (
                        <button
                          onClick={() => handleOpenVerificationModal(upi)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                          title="View and verify UPI ID"
                        >
                          <Shield className="h-3 w-3 mr-1" />
                          Verify
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* UPI Verification Modal */}
      {showVerificationModal && selectedUpi && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-primary-600" />
                  Verify UPI ID
                </h3>
                <button
                  onClick={handleCloseVerificationModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              {success && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">{success}</span>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-dark-bg-primary p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Recipient Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Name:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-dark-text-primary">
                        {selectedUpi.name}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Type:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-dark-text-primary capitalize">
                        {selectedUpi.type}
                      </span>
                    </div>
                    {selectedUpi.organization_name && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Organization:</span>
                        <span className="ml-2 text-gray-900 dark:text-dark-text-primary">
                          {selectedUpi.organization_name}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Email:</span>
                      <span className="ml-2 text-gray-900 dark:text-dark-text-primary">
                        {selectedUpi.email || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Contact:</span>
                      <span className="ml-2 text-gray-900 dark:text-dark-text-primary">
                        {selectedUpi.contact || '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                    <Smartphone className="h-4 w-4 mr-2" />
                    UPI ID
                  </h4>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">UPI ID:</span>
                    <div className="text-base font-mono text-gray-900 dark:text-dark-text-primary mt-1">
                      {selectedUpi.upi_id || '-'}
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                      <p className="font-medium mb-1">Verification Guidelines:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Verify that the UPI ID format is correct (username@bankname)</li>
                        <li>Ensure the UPI ID belongs to the recipient</li>
                        <li>Once verified, the recipient will be eligible for UPI payouts</li>
                        <li>UPI payouts will be preferred over bank account transfers</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-dark-border">
                  <button
                    onClick={handleCloseVerificationModal}
                    disabled={verifying}
                    className="px-4 py-2 bg-gray-200 dark:bg-dark-bg-primary text-gray-700 dark:text-dark-text-primary rounded-lg hover:bg-gray-300 dark:hover:bg-dark-bg-secondary transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className="h-4 w-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleVerifyUpi}
                    disabled={verifying}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {verifying ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        <span>Verify UPI ID</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpiIdDetailsTab;
